/**
 * Documents API - Document management and search.
 */

import apiClient, { type RequestConfig } from './client';
import type {
  Document,
  DocumentSearchParams,
  PaginatedResponse,
  PrivilegeStatus,
  SearchRequest,
} from './types';
import type { SearchResponse } from '@/features/search/types';

const ENDPOINT = '/documents';

export interface BulkDocumentFailure {
  document_id: string;
  reason: string;
}

export interface BulkUpdateResult {
  success: boolean;
  updated: number;
  failed: number;
  updated_ids: string[];
  failures: BulkDocumentFailure[];
}

export interface BulkDeleteResult {
  success: boolean;
  deleted: number;
  failed: number;
  deleted_ids: string[];
  failures: BulkDocumentFailure[];
}

export interface DocumentUpdatePayload {
  custodian?: string;
  sha256_hash?: string;
  md5_hash?: string;
  status?: string;
  relevance_score?: number;
  is_responsive?: boolean;
  privilege_status?: PrivilegeStatus;
  exclusion_reason?: string | null;
  exclusion_details?: string | null;
}

export interface DocumentUpdateResponse {
  id: string;
  updated_fields: string[];
  message: string;
}

export const documentsApi = {
  /**
   * List documents for a matter with filtering and pagination.
   */
  async list(
    matterId: string,
    params?: DocumentSearchParams,
    config?: RequestConfig
  ): Promise<PaginatedResponse<Document>> {
    const { data } = await apiClient.get<PaginatedResponse<Document>>(
      `${ENDPOINT}`,
      {
        ...config,
        params: { matter_id: matterId, ...params } as RequestConfig['params'],
      }
    );
    return data;
  },

  /**
   * Get a single document by ID.
   */
  async get(id: string, config?: RequestConfig): Promise<Document> {
    const { data } = await apiClient.get<Document>(`${ENDPOINT}/${id}`, config);
    return data;
  },

  /**
   * Get document text content.
   */
  async getText(id: string, config?: RequestConfig): Promise<{
    native_text?: string;
    ocr_text?: string;
    combined_text: string;
    text?: string;
    language?: string;
    confidence?: number;
  }> {
    const { data } = await apiClient.get<{
      native_text?: string;
      ocr_text?: string;
      combined_text?: string;
      text?: string;
      language?: string;
      confidence?: number;
    }>(`${ENDPOINT}/${id}/text`, config);
    const combinedText =
      data.combined_text ??
      data.text ??
      data.native_text ??
      data.ocr_text ??
      '';
    return {
      ...data,
      combined_text: combinedText,
      text: data.text ?? combinedText,
    };
  },

  async getOcrReview(id: string, config?: RequestConfig): Promise<{
    document_id: string;
    filename: string;
    requires_human_review: boolean;
    review_reason?: string;
    current_text: string;
    ocr_review: NonNullable<Document['ocr_review']>;
  }> {
    const { data } = await apiClient.get<{
      document_id: string;
      filename: string;
      requires_human_review: boolean;
      review_reason?: string;
      current_text: string;
      ocr_review: NonNullable<Document['ocr_review']>;
    }>(`${ENDPOINT}/${id}/ocr-review`, config);
    return data;
  },

  async resolveOcrReview(
    id: string,
    payload: {
      resolved_text?: string;
      resolution_notes?: string;
      clear_review?: boolean;
    },
    config?: RequestConfig
  ): Promise<{
    status: string;
    document_id: string;
    cleared_review: boolean;
    text_replaced: boolean;
  }> {
    const { data } = await apiClient.post<{
      status: string;
      document_id: string;
      cleared_review: boolean;
      text_replaced: boolean;
    }>(`${ENDPOINT}/${id}/ocr-review/resolve`, payload, config);
    return data;
  },

  /**
   * Get document metadata and processing info.
   */
  async getMetadata(id: string, config?: RequestConfig): Promise<{
    file_metadata: Record<string, unknown>;
    processing_history: Array<{
      type: string;
      status: string;
      timestamp: string;
    }>;
    chain_of_custody: Array<{
      action: string;
      timestamp: string;
      user: string;
      hash: string;
    }>;
  }> {
    const { data } = await apiClient.get<{
      file_metadata: Record<string, unknown>;
      processing_history: Array<{
        type: string;
        status: string;
        timestamp: string;
      }>;
      chain_of_custody: Array<{
        action: string;
        timestamp: string;
        user: string;
        hash: string;
      }>;
    }>(`${ENDPOINT}/${id}/metadata`, config);
    return data;
  },

  /**
   * Update document metadata fields exposed by the PATCH /documents/{id} API.
   */
  async update(
    id: string,
    updates: DocumentUpdatePayload,
    config?: RequestConfig
  ): Promise<DocumentUpdateResponse> {
    const { data } = await apiClient.patch<DocumentUpdateResponse>(`${ENDPOINT}/${id}`, updates, config);
    return data;
  },

  /**
   * Bulk update documents.
   * SOTA 2026: Requires decision_rationale for defensibility.
   */
  async bulkUpdate(
    ids: string[],
    updates: {
      tags?: string[];
      is_responsive?: boolean;
      privilege_status?: PrivilegeStatus;
      exclusion_reason?: string | null;
      exclusion_details?: string | null;
    },
    rationale?: string,
    config?: RequestConfig
  ): Promise<BulkUpdateResult> {
    const { data } = await apiClient.post<BulkUpdateResult>(
      `${ENDPOINT}/bulk-update`,
      { 
        ids, 
        updates, 
        decision_rationale: rationale || "User initiated bulk update from command center"
      },
      config
    );
    return data;
  },

  /**
   * Search documents using semantic, keyword, or hybrid search.
   */
  async search(request: SearchRequest, config?: RequestConfig): Promise<SearchResponse> {
    // SOTA 2026: Route to unified search gateway (mounted at /api/search)
    const { data } = await apiClient.post<SearchResponse>('/search/advanced', request, config);
    return data;
  },

  /**
   * Get similar documents (for dedup/clustering).
   */
  async getSimilar(
    id: string,
    params?: {
      limit?: number;
      min_similarity?: number;
    },
    config?: RequestConfig
  ): Promise<Array<{
    document: Document;
    similarity: number;
  }>> {
    const { data } = await apiClient.get<Array<{
      document: Document;
      similarity: number;
    }>>(`${ENDPOINT}/${id}/similar`, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Get document topics and entities.
   */
  async getAnalysis(id: string, config?: RequestConfig): Promise<{
    topics: Array<{ topic: string; confidence: number }>;
    entities: Array<{ entity: string; type: string; count: number }>;
    sentiment: { label: string; score: number };
    key_phrases: string[];
  }> {
    const { data } = await apiClient.get<{
      topics: Array<{ topic: string; confidence: number }>;
      entities: Array<{ entity: string; type: string; count: number }>;
      sentiment: { label: string; score: number };
      key_phrases: string[];
    }>(`${ENDPOINT}/${id}/analysis`, config);
    return data;
  },

  /**
   * Download original document.
   */
  async download(id: string, config?: RequestConfig): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`${ENDPOINT}/${id}/download`, config);
    return data;
  },

  /**
   * Get download URL for a document (with auth header).
   * Returns the blob as a data URL for use with PDF viewers.
   */
  async getDownloadUrl(id: string, config?: RequestConfig): Promise<string> {
    const blob = await apiClient.getBlob(`${ENDPOINT}/${id}/download`, config);
    return URL.createObjectURL(blob);
  },

  /**
   * Get document thumbnail/preview metadata and multimodal indicators.
   */
  async getPreview(
    id: string,
    options?: {
      page?: number;
      width?: number;
      height?: number;
    },
    config?: RequestConfig
  ): Promise<{
    id: string;
    filename: string;
    file_type: string;
    page: number;
    has_preview: boolean;
    preview_type?: 'text' | 'multimodal';
    content?: string;
    truncated?: boolean;
    multimodal?: {
      has_visual_evidence: boolean;
      artifact_id?: string;
      artifact_type?: string;
    };
  }> {
    const { data } = await apiClient.get<{
      id: string;
      filename: string;
      file_type: string;
      page: number;
      has_preview: boolean;
      preview_type?: 'text' | 'multimodal';
      content?: string;
      truncated?: boolean;
      multimodal?: {
        has_visual_evidence: boolean;
        artifact_id?: string;
        artifact_type?: string;
      };
    }>(`${ENDPOINT}/${id}/preview`, {
      ...config,
      params: options as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Upload documents to a matter.
   */
  async upload(
    matterId: string,
    files: File[],
    options?: {
      custodian?: string;
      context_type?: string;
      auto_process?: boolean;
      sha256_hash?: string;
      md5_hash?: string;
    },
    onProgress?: (progress: number) => void,
    config?: RequestConfig
  ): Promise<{
    uploaded: number;
    failed: number;
    document_ids: string[];
    errors: Array<{ filename: string; error: string }>;
  }> {
    const formData = new FormData();
    files.forEach((file) => {
      // Use webkitRelativePath as filename if available to preserve folder structure
      const fileName = (file as any).webkitRelativePath || file.name;
      formData.append('files', file, fileName);
    });

    if (options?.custodian) {
      formData.append('custodian', options.custodian);
    }
    if (options?.context_type) {
      formData.append('context_type', options.context_type);
    }
    if (options?.auto_process !== undefined) {
      formData.append('auto_process', String(options.auto_process));
    }
    if (options?.sha256_hash) {
      formData.append('sha256_hash', options.sha256_hash);
    }
    if (options?.md5_hash) {
      formData.append('md5_hash', options.md5_hash);
    }

    const { data } = await apiClient.upload<{
      uploaded: number;
      failed: number;
      document_ids: string[];
      errors: Array<{ filename: string; error: string }>;
    }>(`${ENDPOINT}/matter/${matterId}/upload`, formData, onProgress, config);

    return data;
  },

  /**
   * Batch delete documents.
   */
  async bulkDelete(
    ids: string[],
    rationale?: string,
    config?: RequestConfig
  ): Promise<BulkDeleteResult> {
    const { data } = await apiClient.post<BulkDeleteResult>(
      `${ENDPOINT}/bulk-delete`,
      {
        ids,
        decision_rationale: rationale || 'User initiated bulk delete from command center',
      },
      config
    );
    return data;
  },

  /**
   * List all documents in a system (for privilege review queue cross-matter).
   * Note: This is usually called within a specific matter but API supports tenant-wide list if matterId omitted.
   */
  async listAll(
    params?: DocumentSearchParams,
    config?: RequestConfig
  ): Promise<PaginatedResponse<Document>> {
    const { data } = await apiClient.get<PaginatedResponse<Document>>(
      `${ENDPOINT}`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Verify or reject a privilege flag.
   */
  async verifyPrivilege(
    id: string,
    confirmed: boolean,
    notes?: string,
    config?: RequestConfig
  ): Promise<Document> {
    const { data } = await apiClient.post<Document>(
      `${ENDPOINT}/${id}/verify-privilege`,
      { confirmed, notes },
      config
    );
    return data;
  },

  /**
   * Get document statistics for a matter.
   */
  async getStats(
    matterId: string,
    config?: RequestConfig
  ): Promise<{
    total: number;
    by_status: Record<string, number>;
    by_type: Record<string, number>;
    by_responsiveness: { responsive: number; non_responsive: number; unreviewed: number };
    by_privilege: { privileged: number; non_privileged: number };
    total_bytes: number;
    date_range: { min: string; max: string } | null;
  }> {
    const { data } = await apiClient.get<{
      total: number;
      by_status: Record<string, number>;
      by_type: Record<string, number>;
      by_responsiveness: { responsive: number; non_responsive: number; unreviewed: number };
      by_privilege: { privileged: number; non_privileged: number };
      total_bytes: number;
      date_range: { min: string; max: string } | null;
    }>(`${ENDPOINT}/matter/${matterId}/stats`, config);
    return data;
  },

  /**
   * List distinct custodians for a matter.
   * Returns existing custodian names for autocomplete.
   */
  async listCustodians(
    matterId: string,
    config?: RequestConfig
  ): Promise<string[]> {
    const { data } = await apiClient.get<{ custodians: string[] }>(
      `${ENDPOINT}/custodians`,
      {
        ...config,
        params: { matter_id: matterId },
      }
    );
    return data.custodians;
  },
};

export default documentsApi;
